import SettingConfig from '@module/config/setting';
import { usePostLiveSponMutation } from '@module/queries/live/liveQueryHooks';
import useAutoSponStore from '@module/stores/useAutoSponStore';
import catchAllErrors from '@module/utils/catchAllErrors';
import delay from '@module/utils/delay';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useFetchLivePlayMutationStore } from '../../stores/useFetchLivePlayMutationStore';
import useSocketStore from '../../stores/useSocketStore';
import { syncLoginInfoQueryData } from '../auth/syncLoginInfoQueryData';

/**
 * 두산선물, 연속선물
 */

interface Props {
  onError: (message: string) => void;
}

const useAutoSpon = ({ onError }: Props) => {
  const queryClient = useQueryClient();
  const playInfo = useFetchLivePlayMutationStore((state) => state.data);
  const mediaCode = playInfo?.media.code;
  const userIdx = playInfo?.media.userIdx;
  const token = useSocketStore((state) => state.token);
  const { isSponTriggered, resetIsSponTriggered, sponState } = useAutoSponStore();

  const lastUpdatedCount = useRef(0); // 진행 중인 연속 후원 존재 시, 덮어쓰기 위함

  const { mutateAsync: mutateSpon } = usePostLiveSponMutation(
    {
      onSuccess: (data) => {
        syncLoginInfoQueryData(queryClient, data.loginInfo);
      },
    },
    SettingConfig.environment.includes('fanza'),
  );

  useEffect(() => {
    if (!token || !userIdx || !mediaCode) return;

    if (!isSponTriggered) return;

    const isBroadcastExcel = playInfo?.excelBroadcast?.stat === 'on';

    // 후원을 시작한 방송을 벗어나면 즉시 중단. 언마운트 의존(옛 isRouteChanged) 방식은 쓰지 않는다.
    // play 트리는 이탈 시 언마운트가 아니라 React Activity로 hidden 처리되는데, hide 시 이펙트 cleanup이
    // 돌아 언마운트 플래그가 켜진 뒤 show로 돌아와도 리셋되지 않아 이후 후원을 영구히 막았다(회귀 원인).
    // - /play/A→/play/B(방송 갈아타기): PlayContainer 재사용으로 언마운트가 없음 → media.code 대조로 감지.
    // - /play 밖으로 이탈(Activity hidden 포함): React 트리와 무관한 실제 URL(pathname)로 감지.
    const isBroadcastLeft = () =>
      !window.location.pathname.includes('/play/') ||
      useFetchLivePlayMutationStore.getState().data?.media.code !== mediaCode;

    if (sponState?.type === 'dusan') {
      const dusanSpon = async () => {
        if (
          !sponState?.start ||
          !sponState?.end ||
          !sponState?.continueTime ||
          (isBroadcastExcel && !sponState?.excelBj) ||
          (isBroadcastExcel && !sponState?.excelType)
        )
          return;

        try {
          const startNum = Number(sponState.start);
          const endNum = Number(sponState.end);
          if (Number.isNaN(startNum) || Number.isNaN(endNum)) {
            throw new Error('sponState has NaN.');
          }
          resetIsSponTriggered();
          let count = 0;
          const isIncreasing = endNum >= startNum;
          for (let i = startNum; isIncreasing ? i <= endNum : i >= endNum; isIncreasing ? i++ : i--) {
            if (isBroadcastLeft()) break;
            if (lastUpdatedCount.current < count) break;
            await mutateSpon({
              heart: i,
              mediaCode,
              ...(i === startNum && { message: sponState?.message || '' }),
              token,
              toUserIdx: userIdx,
              ...(isBroadcastExcel && {
                excelBj: sponState.excelBj,
                excelType: sponState.excelType,
              }),
            });
            count += 1;
            lastUpdatedCount.current = count;
            // eslint-disable-next-line no-await-in-loop
            await delay(sponState.continueTime * 1000);
          }
        } catch (error) {
          catchAllErrors(error);
          if (error instanceof Error) {
            onError(error.message);
          }
        }
      };

      dusanSpon();
      return;
    }

    if (sponState?.type === 'continue') {
      const continueSpon = async () => {
        if (
          !sponState?.count ||
          !sponState?.heart ||
          !sponState?.continueTime ||
          (isBroadcastExcel && !sponState?.excelBj) ||
          (isBroadcastExcel && !sponState?.excelType)
        )
          return;
        try {
          if (Number.isNaN(Number(sponState.count)) || Number.isNaN(Number(sponState.heart))) {
            throw new Error('sponState has NaN.');
          }
          resetIsSponTriggered();
          let count = 0;
          for (let i = 0; i < Number(sponState.count); i += 1) {
            if (isBroadcastLeft()) break;
            if (lastUpdatedCount.current < count) break;
            await mutateSpon({
              heart: Number(sponState.heart),
              mediaCode,
              ...(i === 0 && { message: sponState?.message || '' }),
              token,
              toUserIdx: userIdx,
              ...(isBroadcastExcel && {
                excelBj: sponState.excelBj,
                excelType: sponState.excelType,
              }),
            });
            count += 1;
            lastUpdatedCount.current = count;
            // eslint-disable-next-line no-await-in-loop
            await delay(sponState.continueTime * 1000);
          }
        } catch (error) {
          catchAllErrors(error);
          if (error instanceof Error) {
            onError(error.message);
          }
        }
      };

      continueSpon();
    }
  }, [isSponTriggered, mediaCode, mutateSpon, sponState, token, userIdx, playInfo?.excelBroadcast?.stat, onError]);
};

export default useAutoSpon;
